import { useEffect, useState } from 'react';
import type {
  ImportarChecklists,
  ImportarOportunidades,
  ImportarProyectos,
  Instantanea,
  ResumenImportacion,
  ResumenImportacionChecklists,
  ResumenImportacionCrm,
  ResumenImportacionProyectos,
} from '@matias-asset-os/contracts';
import { t, type Idioma } from '@matias-asset-os/i18n';
import { api, ErrorApi } from '../api.js';

/**
 * IMPORTAR LA INSTANTÁNEA DE ARRANQUE
 *
 * La pantalla que se usa una vez y desaparece: en cuanto los datos entran, la
 * cartera deja de estar vacía y el servidor deja de ofrecer esta entrada.
 *
 * El fichero NO se sube a ningún sitio intermedio: se lee en el navegador de
 * quien importa y se envía a la propia aplicación, con su sesión. La base de
 * datos no se toca desde fuera en ningún momento.
 *
 * La contraseña inicial se pide aquí y no viaja dentro del fichero. Un fichero
 * con los datos de la empresa acaba copiado en una carpeta compartida; una
 * contraseña dentro de él acabaría copiada también.
 */

const FILAS: Array<[keyof ResumenImportacion, string]> = [
  ['usuariosCreados', 'import.usuariosCreados'],
  ['usuariosReutilizados', 'import.usuariosReutilizados'],
  ['propiedades', 'import.propiedades'],
  ['proveedores', 'import.proveedores'],
  ['gastos', 'import.gastos'],
  ['horas', 'import.horas'],
  ['incidencias', 'import.incidencias'],
  ['eventos', 'import.eventos'],
  ['mantenimiento', 'import.mantenimiento'],
  ['estancias', 'import.estancias'],
  ['documentos', 'import.documentos'],
  ['historial', 'import.historial'],
];

export function Importacion({ idioma, onVolver }: { idioma: Idioma; onVolver: () => void }) {
  /*
   * La instantanea de arranque solo cabe en una empresa vacia; los proyectos
   * llegan despues, sobre una que ya tiene casas. Por eso la pantalla pregunta
   * en que punto esta en vez de ofrecer las dos cosas siempre.
   */
  const [vacia, setVacia] = useState<boolean | null>(null);
  const [instantanea, setInstantanea] = useState<Instantanea | null>(null);
  const [nombreFichero, setNombreFichero] = useState('');
  const [password, setPassword] = useState('');
  const [enCurso, setEnCurso] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resumen, setResumen] = useState<ResumenImportacion | null>(null);

  useEffect(() => {
    api
      .importacionEstado()
      .then((e) => setVacia(e.vacia))
      .catch(() => setVacia(false));
  }, []);

  const elegir = async (fichero: File | undefined) => {
    setError(null);
    setResumen(null);
    setInstantanea(null);
    setNombreFichero('');
    if (!fichero) return;
    try {
      const datos = JSON.parse(await fichero.text()) as Instantanea;
      if (datos.version !== 1) throw new Error('version');
      setInstantanea(datos);
      setNombreFichero(fichero.name);
    } catch {
      setError(t(idioma, 'import.ficheroInvalido'));
    }
  };

  const importar = () => {
    if (!instantanea || password.length < 8) return;
    setEnCurso(true);
    setError(null);
    api
      .importarInstantanea(instantanea, password)
      .then((r) => {
        setResumen(r.resumen);
        setInstantanea(null);
        setPassword('');
      })
      .catch((f: unknown) =>
        setError(f instanceof ErrorApi ? t(idioma, f.mensajeClave) : t(idioma, 'error.servidor')),
      )
      .finally(() => setEnCurso(false));
  };

  const cuenta = (lista: unknown[] | undefined) => (lista ? lista.length : 0);

  return (
    <main>
      <div className="cabecera-seccion">
        <div>
          <h1>{t(idioma, 'import.titulo')}</h1>
        </div>
        <div className="acciones">
          <button type="button" className="secundario" onClick={onVolver}>
            {t(idioma, 'app.volver')}
          </button>
        </div>
      </div>

      <p className="prosa suave">{t(idioma, 'import.intro')}</p>

      {error && (
        <p className="aviso-demo" role="alert">
          {error}
        </p>
      )}

      {vacia === false && !resumen && (
        <p className="suave">{t(idioma, 'import.carteraConDatos')}</p>
      )}

      {resumen ? (
        <section className="tarjeta">
          <h2>{t(idioma, 'import.hecho')}</h2>
          <table className="tabla">
            <tbody>
              {FILAS.map(([campo, clave]) => (
                <tr key={campo}>
                  <td>{t(idioma, clave)}</td>
                  <td className="numerico">{resumen[campo]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ) : vacia !== false ? (
        <section className="tarjeta">
          <form
            className="formulario"
            style={{ maxWidth: 'none' }}
            onSubmit={(ev) => {
              ev.preventDefault();
              importar();
            }}
          >
            <div className="campo">
              <label htmlFor="imp-fichero">{t(idioma, 'import.fichero')}</label>
              <input
                id="imp-fichero"
                type="file"
                accept="application/json,.json"
                onChange={(ev) => void elegir(ev.target.files?.[0])}
              />
            </div>

            {instantanea && (
              <p className="suave">
                {nombreFichero} · {t(idioma, 'import.propiedades')}:{' '}
                <span className="numerico">{cuenta(instantanea.propiedades)}</span> ·{' '}
                {t(idioma, 'import.incidencias')}:{' '}
                <span className="numerico">{cuenta(instantanea.incidencias)}</span> ·{' '}
                {t(idioma, 'import.gastos')}:{' '}
                <span className="numerico">{cuenta(instantanea.gastos)}</span> ·{' '}
                {t(idioma, 'import.horas')}:{' '}
                <span className="numerico">{cuenta(instantanea.horas)}</span>
              </p>
            )}

            <div className="campo">
              <label htmlFor="imp-password">{t(idioma, 'import.password')}</label>
              <input
                id="imp-password"
                type="password"
                autoComplete="new-password"
                value={password}
                minLength={8}
                onChange={(ev) => setPassword(ev.target.value)}
                required
              />
              <p className="suave" style={{ fontSize: '0.8125rem' }}>
                {t(idioma, 'import.passwordNota')}
              </p>
            </div>

            <button
              type="submit"
              className="acceso-entrar"
              disabled={!instantanea || password.length < 8 || enCurso}
            >
              {enCurso ? t(idioma, 'import.importando') : t(idioma, 'import.importar')}
            </button>
          </form>
        </section>
      ) : null}

      <ImportarProyectosPanel idioma={idioma} />
      <ImportarChecklistsPanel idioma={idioma} />
      <ImportarOportunidadesPanel idioma={idioma} />
    </main>
  );
}

// --- Proyectos --------------------------------------------------------------

const FILAS_PROYECTOS: Array<[keyof ResumenImportacionProyectos, string]> = [
  ['equipos', 'import.proyEquipos'],
  ['proyectos', 'import.proyProyectos'],
  ['secciones', 'import.proySecciones'],
  ['tareas', 'import.proyTareas'],
  ['subtareas', 'import.proySubtareas'],
  ['asignaciones', 'import.proyAsignaciones'],
  ['etiquetas', 'import.proyEtiquetas'],
  ['yaEstaban', 'import.proyYaEstaban'],
];

/**
 * Traer los proyectos del gestor anterior.
 *
 * Se puede lanzar mas de una vez sin miedo: el servidor reconoce por su
 * identificador de origen lo que ya esta y lo cuenta aparte en vez de
 * duplicarlo. Por eso no hace falta preguntar «seguro?» antes de repetir.
 */
function ImportarProyectosPanel({ idioma }: { idioma: Idioma }) {
  const [fichero, setFichero] = useState<ImportarProyectos | null>(null);
  const [nombre, setNombre] = useState('');
  const [enCurso, setEnCurso] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resumen, setResumen] = useState<ResumenImportacionProyectos | null>(null);

  const cuentaTareas = (datos: ImportarProyectos) =>
    datos.equipos.reduce(
      (n, e) => n + e.proyectos.reduce((m, p) => m + p.tareas.length, 0),
      0,
    );
  const cuentaProyectos = (datos: ImportarProyectos) =>
    datos.equipos.reduce((n, e) => n + e.proyectos.length, 0);

  const elegir = async (f: File | undefined) => {
    setError(null);
    setResumen(null);
    setFichero(null);
    setNombre('');
    if (!f) return;
    try {
      const datos = JSON.parse(await f.text()) as ImportarProyectos;
      if (!datos.origen || !Array.isArray(datos.equipos)) throw new Error('forma');
      setFichero(datos);
      setNombre(f.name);
    } catch {
      setError(t(idioma, 'import.ficheroInvalido'));
    }
  };

  return (
    <section className="tarjeta">
      <h2>{t(idioma, 'import.proyTitulo')}</h2>
      <p className="suave">{t(idioma, 'import.proyIntro')}</p>

      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}

      {resumen ? (
        <>
          <table className="tabla">
            <tbody>
              {FILAS_PROYECTOS.map(([campo, clave]) => (
                <tr key={campo}>
                  <td>{t(idioma, clave)}</td>
                  <td className="numerico">{resumen[campo]}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {/*
            Las personas que no se han podido resolver se dicen con nombre. Una
            tarea sin responsable por un correo que no cuadra es exactamente lo
            que nadie descubre hasta tres semanas despues.
          */}
          {resumen.personasSinResolver.length > 0 && (
            <p className="error" role="alert">
              {t(idioma, 'import.proySinResolver')}: {resumen.personasSinResolver.join(', ')}
            </p>
          )}
        </>
      ) : (
        <>
          <div className="campo">
            <label htmlFor="imp-proyectos">{t(idioma, 'import.fichero')}</label>
            <input
              id="imp-proyectos"
              type="file"
              accept="application/json,.json"
              onChange={(ev) => void elegir(ev.target.files?.[0])}
            />
          </div>

          {fichero && (
            <p className="suave">
              {nombre} · {t(idioma, 'import.proyProyectos')}:{' '}
              <span className="numerico">{cuentaProyectos(fichero)}</span> ·{' '}
              {t(idioma, 'import.proyTareas')}:{' '}
              <span className="numerico">{cuentaTareas(fichero)}</span>
            </p>
          )}

          <div className="fila-flex">
            <button
              type="button"
              disabled={!fichero || enCurso}
              onClick={() => {
                if (!fichero) return;
                setEnCurso(true);
                setError(null);
                api
                  .importarProyectos(fichero)
                  .then((r) => {
                    setResumen(r.resumen);
                    setFichero(null);
                  })
                  .catch((f: unknown) =>
                    setError(
                      f instanceof ErrorApi
                        ? t(idioma, f.mensajeClave)
                        : t(idioma, 'error.servidor'),
                    ),
                  )
                  .finally(() => setEnCurso(false));
              }}
            >
              {enCurso ? t(idioma, 'import.importando') : t(idioma, 'import.importar')}
            </button>
          </div>
        </>
      )}
    </section>
  );
}

// --- Checklists ---------------------------------------------------------------

/**
 * Traer las plantillas de visita del gestor anterior.
 *
 * Se reconocen por su NOMBRE, asi que repetir la importacion no duplica nada:
 * a una plantilla que ya esta solo se le anaden los items que le falten. Eso
 * permite completar en una segunda pasada una plantilla que llego a medias
 * —porque la fuente estaba incompleta— sin borrar lo que se haya escrito.
 */
function ImportarChecklistsPanel({ idioma }: { idioma: Idioma }) {
  const [fichero, setFichero] = useState<ImportarChecklists | null>(null);
  const [nombre, setNombre] = useState('');
  const [enCurso, setEnCurso] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resumen, setResumen] = useState<ResumenImportacionChecklists | null>(null);

  const elegir = async (f: File | undefined) => {
    setError(null);
    setResumen(null);
    setFichero(null);
    setNombre('');
    if (!f) return;
    try {
      const datos = JSON.parse(await f.text()) as ImportarChecklists;
      if (!Array.isArray(datos.plantillas)) throw new Error('forma');
      setFichero(datos);
      setNombre(f.name);
    } catch {
      setError(t(idioma, 'import.ficheroInvalido'));
    }
  };

  const items = (datos: ImportarChecklists) =>
    datos.plantillas.reduce((n, p) => n + p.items.length, 0);

  return (
    <section className="tarjeta">
      <h2>{t(idioma, 'import.checkTitulo')}</h2>
      <p className="suave">{t(idioma, 'import.checkIntro')}</p>

      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}

      {resumen ? (
        <table className="tabla">
          <tbody>
            <tr>
              <td>{t(idioma, 'import.checkPlantillas')}</td>
              <td className="numerico">{resumen.plantillas}</td>
            </tr>
            <tr>
              <td>{t(idioma, 'import.checkItems')}</td>
              <td className="numerico">{resumen.items}</td>
            </tr>
            <tr>
              <td>{t(idioma, 'import.proyYaEstaban')}</td>
              <td className="numerico">
                {resumen.plantillasYaEstaban} / {resumen.itemsYaEstaban}
              </td>
            </tr>
          </tbody>
        </table>
      ) : (
        <>
          <div className="campo">
            <label htmlFor="imp-checklists">{t(idioma, 'import.fichero')}</label>
            <input
              id="imp-checklists"
              type="file"
              accept="application/json,.json"
              onChange={(ev) => void elegir(ev.target.files?.[0])}
            />
          </div>

          {fichero && (
            <p className="suave">
              {nombre} · {t(idioma, 'import.checkPlantillas')}:{' '}
              <span className="numerico">{fichero.plantillas.length}</span> ·{' '}
              {t(idioma, 'import.checkItems')}:{' '}
              <span className="numerico">{items(fichero)}</span>
            </p>
          )}

          <div className="fila-flex">
            <button
              type="button"
              disabled={!fichero || enCurso}
              onClick={() => {
                if (!fichero) return;
                setEnCurso(true);
                setError(null);
                api
                  .importarChecklists(fichero)
                  .then((r) => {
                    setResumen(r.resumen);
                    setFichero(null);
                  })
                  .catch((f: unknown) =>
                    setError(
                      f instanceof ErrorApi
                        ? t(idioma, f.mensajeClave)
                        : t(idioma, 'error.servidor'),
                    ),
                  )
                  .finally(() => setEnCurso(false));
              }}
            >
              {enCurso ? t(idioma, 'import.importando') : t(idioma, 'import.importar')}
            </button>
          </div>
        </>
      )}
    </section>
  );
}

// --- Oportunidades del CRM anterior -----------------------------------------

const euros = (cents: number, idioma: Idioma) =>
  new Intl.NumberFormat(idioma === 'es' ? 'es-ES' : idioma === 'de' ? 'de-DE' : 'en-GB', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(cents / 100);

/**
 * Traer el embudo comercial del CRM anterior.
 *
 * Se puede repetir cuantas veces haga falta: cada oportunidad viaja con el
 * identificador que tenia en su sistema de origen, y la segunda pasada
 * actualiza en vez de duplicar. Es lo que permite exportar un martes, revisar,
 * y volver a exportar el jueves.
 *
 * El desglose por embudo se ensena SIEMPRE, no solo cuando algo falla. Un
 * import que dice «97 filas correctas» y nada mas obliga a ir al CRM a contar a
 * mano para saber si de verdad esta todo donde tiene que estar.
 */
function ImportarOportunidadesPanel({ idioma }: { idioma: Idioma }) {
  const [fichero, setFichero] = useState<ImportarOportunidades | null>(null);
  const [nombre, setNombre] = useState('');
  const [enCurso, setEnCurso] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resumen, setResumen] = useState<ResumenImportacionCrm | null>(null);

  const elegir = async (f: File | undefined) => {
    setError(null);
    setResumen(null);
    setFichero(null);
    setNombre('');
    if (!f) return;
    try {
      const datos = JSON.parse(await f.text()) as ImportarOportunidades;
      if (!datos.fuente || !Array.isArray(datos.oportunidades)) throw new Error('forma');
      setFichero(datos);
      setNombre(f.name);
    } catch {
      setError(t(idioma, 'import.ficheroInvalido'));
    }
  };

  const importar = () => {
    if (!fichero) return;
    setEnCurso(true);
    setError(null);
    api
      .importarOportunidades(fichero)
      .then((r) => {
        setResumen(r.resumen);
        setFichero(null);
        setNombre('');
      })
      .catch((f: unknown) =>
        setError(f instanceof ErrorApi ? t(idioma, f.mensajeClave) : t(idioma, 'error.servidor')),
      )
      .finally(() => setEnCurso(false));
  };

  return (
    <section className="tarjeta">
      <h2>{t(idioma, 'import.crmTitulo')}</h2>
      <p className="suave">{t(idioma, 'import.crmIntro')}</p>

      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}

      {resumen ? (
        <>
          <table className="tabla">
            <tbody>
              <tr>
                <td>{t(idioma, 'import.crmCreadas')}</td>
                <td className="numerico">{resumen.creadas}</td>
              </tr>
              <tr>
                <td>{t(idioma, 'import.crmActualizadas')}</td>
                <td className="numerico">{resumen.actualizadas}</td>
              </tr>
              <tr>
                <td>{t(idioma, 'import.crmTotal')}</td>
                <td className="numerico">{resumen.total}</td>
              </tr>
              <tr>
                <td>{t(idioma, 'import.crmSuma')}</td>
                <td className="numerico">{euros(resumen.sumaCents, idioma)}</td>
              </tr>
              {/*
                La historia, contada: en la segunda subida del mismo fichero estas
                dos filas dicen 0, y eso es justo lo que hay que poder ver para
                saber que no se ha duplicado nada.
              */}
              <tr>
                <td>{t(idioma, 'import.crmNotas')}</td>
                <td className="numerico">{resumen.notas}</td>
              </tr>
              <tr>
                <td>{t(idioma, 'import.crmTareas')}</td>
                <td className="numerico">{resumen.tareas}</td>
              </tr>
            </tbody>
          </table>
          {resumen.fusionadasPorNombre.length > 0 && (
            <p className="suave">
              {t(idioma, 'import.crmFusionadas', { n: resumen.fusionadasPorNombre.length })}{' '}
              {resumen.fusionadasPorNombre.join(' · ')}
            </p>
          )}

          <h3>{t(idioma, 'import.crmPorEmbudo')}</h3>
          <table className="tabla">
            <thead>
              <tr>
                <th scope="col">{t(idioma, 'crm.embudo')}</th>
                <th scope="col">{t(idioma, 'import.crmTotal')}</th>
                <th scope="col">{t(idioma, 'import.crmSuma')}</th>
              </tr>
            </thead>
            <tbody>
              {resumen.porEmbudo.map((e) => (
                <tr key={e.pipeline}>
                  <td>{e.pipeline}</td>
                  <td className="numerico">{e.total}</td>
                  <td className="numerico">{euros(e.sumaCents, idioma)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {resumen.embudosCreados.length > 0 && (
            <p className="suave">
              {t(idioma, 'import.crmEmbudosCreados')}: {resumen.embudosCreados.join(', ')}
            </p>
          )}
          {resumen.etapasCreadas.length > 0 && (
            <p className="suave">
              {t(idioma, 'import.crmEtapasCreadas')}: {resumen.etapasCreadas.join(' · ')}
            </p>
          )}
          {resumen.responsablesResueltos.length > 0 && (
            <p className="suave">
              {t(idioma, 'import.crmResponsables')}:{' '}
              {resumen.responsablesResueltos.map((r) => `${r.origen} → ${r.usuario}`).join(' · ')}
            </p>
          )}
          {/*
            Los avisos se ensenan como error aunque el import haya entrado: son
            justo lo que hay que mirar, y en gris se leen como decoracion.
          */}
          {resumen.avisos.map((a) => (
            <p className="error" key={a} role="alert">
              {a}
            </p>
          ))}
        </>
      ) : (
        <>
          <div className="campo">
            <label htmlFor="imp-crm">{t(idioma, 'import.fichero')}</label>
            <input
              id="imp-crm"
              type="file"
              accept="application/json,.json"
              onChange={(ev) => void elegir(ev.target.files?.[0])}
            />
          </div>
          {fichero && (
            <p className="suave">
              {nombre} · {fichero.fuente} ·{' '}
              <span className="numerico">{fichero.oportunidades.length}</span>{' '}
              {t(idioma, 'import.crmOportunidades')}
            </p>
          )}
          <button type="button" onClick={importar} disabled={!fichero || enCurso}>
            {enCurso ? t(idioma, 'import.importando') : t(idioma, 'import.importar')}
          </button>
        </>
      )}
    </section>
  );
}
